import { ref, computed, watch, onMounted, nextTick } from "vue";
import { useRoute } from "vue-router";
import { $fetch } from "ofetch";
import { API_BASE_URL, API_KEY } from "../utils/vars";

interface IChartData {
  dateObservedFrom: string;
  dateObservedTo: string;
  IPM25?: {
    value?: number;
    index?: number;
    airQualityObserved?: number;
    airQualityIndex?: number;
  };
  INO2?: {
    value?: number;
    index?: number;
    airQualityObserved?: number;
    airQualityIndex?: number;
  };
  IPM10?: {
    value?: number;
    index?: number;
    airQualityObserved?: number;
    airQualityIndex?: number;
  };
}

interface IApiResponse<T> {
  data: T[];
}

export function useGetApiData() {
  const route = useRoute();

  const chartDataResponse = ref<IChartData[] | undefined>(undefined);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const chartData = computed(() => chartDataResponse.value);

  const querySpecies = computed(() => {
    const species = route.query.species as string;
    return species ? species.toUpperCase() : undefined;
  });

  const fetchChartData = async (networkId: string | undefined) => {
    if (!networkId) {
      console.log("Waiting for site ID...");
      return;
    }

    const params = {
      network: route.query.network,
      siteCode: route.query.siteCode,
      species: route.query.species,
      output: route.query.output,
      startDate: route.query.startDate,
      endDate: route.query.endDate,
      key: API_KEY,
    };

    isLoading.value = true;
    error.value = null;
    try {
      const response = await $fetch<IApiResponse<IChartData>>(
        `${API_BASE_URL}/v2/${networkId}/measurements`,
        {
          params,
          retry: false,
        },
      );

      chartDataResponse.value = response?.data ?? undefined;
    } catch (err: any) {
      console.error("Error fetching air quality data:", err);
      error.value = err instanceof Error ? err.message : "Failed to fetch chart data";
    } finally {
      isLoading.value = false;
    }
  };

  const triggerFetch = computed(() => {
    return `${route.query.network}-${route.query.siteCode}-${route.query.species}-${route.query.output}-${route.query.startDate}-${route.query.endDate}`;
  });

  const onInit = async () => {
    const { network, siteCode, species, output, startDate, endDate } = route.query;
    const hasParams = network || siteCode || species || output || startDate || endDate;
    if (hasParams) {
      await fetchChartData(network as string);
    }
  };

  onMounted(async () => {
    await nextTick();
    setTimeout(async () => {
      watch(
        triggerFetch,
        async () => {
          await onInit();
        },
        {
          immediate: true,
        },
      );
    }, 0);
  });

  return {
    chartData,
    isLoading,
    error,
    querySpecies,
  };
}
